class CountdownTimer extends HTMLElement {
  /**
   * Target date for the countdown (UTC ms)
   * @type {number}
   */
  targetDate;

  /**
   * Countdown elements
   * @type {Object}
   */
  countdownElements;

  constructor() {
    super();

    this.countdownElements = {
      daysTens:
        this.querySelector('[data-digit="days-tens"]') ||
        this.querySelector('#times_day_tens'),
      daysOnes:
        this.querySelector('[data-digit="days-ones"]') ||
        this.querySelector('#times_day_ones'),
      hoursTens:
        this.querySelector('[data-digit="hours-tens"]') ||
        this.querySelector('#times_hour_tens'),
      hoursOnes:
        this.querySelector('[data-digit="hours-ones"]') ||
        this.querySelector('#times_hour_ones'),
      minutesTens:
        this.querySelector('[data-digit="minutes-tens"]') ||
        this.querySelector('#times_minute_tens'),
      minutesOnes:
        this.querySelector('[data-digit="minutes-ones"]') ||
        this.querySelector('#times_minute_ones'),
      secondsTens:
        this.querySelector('[data-digit="seconds-tens"]') ||
        this.querySelector('#times_second_tens'),
      secondsOnes:
        this.querySelector('[data-digit="seconds-ones"]') ||
        this.querySelector('#times_second_ones'),
      daysOnesSymbol: this.querySelector('#times_day_ones_symbol'),
      countdownText: this.querySelector('.components-countdown-text'),
      countdownItem: this.querySelector('.components-countdown-time_items'),
      countdownUnits: this.querySelector('[data-countdown-units]'),
      daysUnit: this.querySelector('[data-unit="days"]'),
    };

    this.targetDate = this.parseTargetDate(
      this.getAttribute('data-date'),
      this.getAttribute('data-timezone') || 'JST'
    );
    this.targetCycle = Math.floor(this.getAttribute('data-cycle')) || 0;
    this.targetEndTip = this.getAttribute('data-endtip') || "Oops! It's over!";
    this.targetDayToHours = this.getAttribute('data-daytohours') === 'true';
    this.hideZeroDays = this.getAttribute('data-hide-zero-days') !== 'false';

    this.updateCountdown();
    this.intervalId = window.setInterval(() => this.updateCountdown(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Parse end date with explicit timezone.
   * Accepts:
   * - 2026/09/30 23:59:59 JST
   * - 2026-09-30T23:59:59+09:00
   * - 2026/09/30 23:59:59 (uses fallbackTimezone)
   */
  parseTargetDate(rawDate, fallbackTimezone = 'JST') {
    if (!rawDate) return NaN;

    const value = String(rawDate).trim();
    if (!value) return NaN;

    if (/^\d{4}-\d{2}-\d{2}T/.test(value) && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
      const parsedIso = Date.parse(value);
      return Number.isNaN(parsedIso) ? NaN : parsedIso;
    }

    const timezoneOffsets = {
      JST: '+09:00',
      KST: '+09:00',
      CST: '+08:00',
      PST: '-08:00',
      PDT: '-07:00',
      EST: '-05:00',
      EDT: '-04:00',
      UTC: '+00:00',
      GMT: '+00:00',
    };

    const match = value.match(
      /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s*([A-Za-z]{2,5}|[+-]\d{2}:?\d{2}))?$/
    );

    if (!match) {
      const fallback = Date.parse(value);
      return Number.isNaN(fallback) ? NaN : fallback;
    }

    const year = match[1];
    const month = match[2].padStart(2, '0');
    const day = match[3].padStart(2, '0');
    const hour = (match[4] || '00').padStart(2, '0');
    const minute = (match[5] || '00').padStart(2, '0');
    const second = (match[6] || '00').padStart(2, '0');
    let zone = (match[7] || fallbackTimezone || 'JST').trim();

    if (/^[+-]\d{4}$/.test(zone)) {
      zone = `${zone.slice(0, 3)}:${zone.slice(3)}`;
    } else if (/^[+-]\d{2}$/.test(zone)) {
      zone = `${zone}:00`;
    } else if (timezoneOffsets[zone.toUpperCase()]) {
      zone = timezoneOffsets[zone.toUpperCase()];
    } else if (!/^[+-]\d{2}:\d{2}$/.test(zone)) {
      zone = timezoneOffsets[(fallbackTimezone || 'JST').toUpperCase()] || '+09:00';
    }

    const iso = `${year}-${month}-${day}T${hour}:${minute}:${second}${zone}`;
    const parsed = Date.parse(iso);
    return Number.isNaN(parsed) ? NaN : parsed;
  }

  updateCountdown() {
    if (Number.isNaN(this.targetDate)) {
      this.resetCountdown();
      return;
    }

    const now = Date.now();
    const timeRemaining = this.targetDate - now;

    if (timeRemaining <= 0) {
      if (this.targetCycle > 0) {
        const cycleTime = 24 * 60 * 60 * 1000 * this.targetCycle;
        const timeDistance = Math.floor(now - this.targetDate);
        const leftTime = timeDistance % cycleTime;
        const time = now + (cycleTime - leftTime);
        this.continueCountdown(time);
      } else {
        this.resetCountdown();
      }
    } else {
      this.continueCountdown(this.targetDate);
    }
  }

  updateElement(element, value) {
    if (element) {
      element.textContent = value;
    }
  }

  resetCountdown() {
    for (const key in this.countdownElements) {
      const el = this.countdownElements[key];
      if (el && el.nodeType === 1 && el.matches?.('.times_digit, [data-digit]')) {
        this.updateElement(el, '0');
      }
    }

    if (this.countdownElements.countdownItem) {
      this.countdownElements.countdownItem.style.display = 'none';
    }

    if (this.countdownElements.countdownUnits) {
      this.countdownElements.countdownUnits.hidden = true;
    }

    this.classList.add('is-ended');
    this.closest('.page-countdown')?.classList.add('is-ended');

    if (this.countdownElements.countdownText) {
      this.countdownElements.countdownText.hidden = false;
      this.updateElement(this.countdownElements.countdownText, this.targetEndTip);
    }
  }

  continueCountdown(time) {
    const now = Date.now();
    const timeRemaining = Math.max(0, time - now);
    const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

    if (this.targetDayToHours && days > 0) {
      const totalHours = days * 24 + hours;
      this.updateElement(
        this.countdownElements.hoursTens,
        totalHours > 99 ? '9' : Math.floor(totalHours / 10).toString()
      );
      this.updateElement(
        this.countdownElements.hoursOnes,
        totalHours > 99 ? '9' : (totalHours % 10).toString()
      );
    } else {
      this.updateElement(
        this.countdownElements.daysTens,
        Math.floor(days / 10).toString()
      );
      this.updateElement(this.countdownElements.daysOnes, (days % 10).toString());
      this.updateElement(
        this.countdownElements.hoursTens,
        Math.floor(hours / 10).toString()
      );
      this.updateElement(this.countdownElements.hoursOnes, (hours % 10).toString());
    }

    this.updateElement(
      this.countdownElements.minutesTens,
      Math.floor(minutes / 10).toString()
    );
    this.updateElement(this.countdownElements.minutesOnes, (minutes % 10).toString());
    this.updateElement(
      this.countdownElements.secondsTens,
      Math.floor(seconds / 10).toString()
    );
    this.updateElement(this.countdownElements.secondsOnes, (seconds % 10).toString());

    if (this.hideZeroDays) {
      const showDays = days > 0 && !this.targetDayToHours;
      if (this.countdownElements.daysTens) {
        this.countdownElements.daysTens.style.display = showDays ? 'inline-block' : 'none';
      }
      if (this.countdownElements.daysOnes) {
        this.countdownElements.daysOnes.style.display = showDays ? 'inline-block' : 'none';
      }
      if (this.countdownElements.daysOnesSymbol) {
        this.countdownElements.daysOnesSymbol.style.display = showDays ? '' : 'none';
        if (this.countdownElements.daysOnesSymbol.parentNode && !this.countdownElements.daysUnit) {
          this.countdownElements.daysOnesSymbol.parentNode.style.display = showDays ? '' : 'none';
        }
      }
      if (this.countdownElements.daysUnit) {
        this.countdownElements.daysUnit.style.display = showDays ? '' : 'none';
      }
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
